import { API_BASE_URL } from '@utils/constants/app-constants';
import { TokenExtension } from '@utils/extensions/token.extension';
import Uppy from '@uppy/core';
import Tus from '@uppy/tus';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { v4 as uuidv4 } from 'uuid';
import { CUSTOM_MODEL_QUERY_KEYS } from './query-keys';

export enum CustomModelDataType {
  TRANSCRIPT = 1,
  TEST = 2,
  AUDIO = 3,
  MANIFEST = 4
}

// Supported file formats for auto-detection
export const SUPPORTED_AUDIO_FORMATS = ['.wav', '.mp3', '.m4a', '.flac'];
// Video uploads are accepted on the AUDIO slot — the server extracts the audio
// track via ffmpeg and stores only the resulting .m4a.
export const SUPPORTED_VIDEO_FORMATS = [
  '.mp4',
  '.mov',
  '.mkv',
  '.webm',
  '.avi'
];
export const SUPPORTED_TRANSCRIPT_FORMATS = ['.vtt', '.srt', '.txt'];
export const SUPPORTED_MANIFEST_FORMATS = ['.jsonl'];

export const ALL_DATASET_FORMATS = [
  ...SUPPORTED_TRANSCRIPT_FORMATS,
  ...SUPPORTED_AUDIO_FORMATS,
  ...SUPPORTED_VIDEO_FORMATS,
  ...SUPPORTED_MANIFEST_FORMATS
];

// Maximum number of files accepted in a single batch. Users can keep adding more
// batches — there is no hard total-per-dataset cap.
export const MAX_DATASET_FILES_PER_BATCH = 100;
// Per-file size ceiling (matches the server-side custom-model limit).
const MAX_DATASET_FILE_SIZE = 10 * 1024 * 1024 * 1024; // 10GB

/**
 * Owns a persistent Uppy instance that uploads custom-model dataset files via the
 * shared resumable TUS endpoint (`/api/v3/files/`). Each file carries
 * `upload_target=custom_model` + `model_id` metadata so the upload-complete hook
 * routes it to the custom-model dataset path instead of creating an STT session.
 *
 * The instance is meant to be driven by the Uppy Dashboard, which renders
 * per-file progress, retry and cancel controls. Transcripts are paired with
 * audio at train time, so files can be uploaded in any order and without a
 * matching transcript.
 */
export type UploadCompleteSummary = { ok: number; failed: number };

export function useCreateDataset(
  modelId: number,
  onUploadComplete?: (summary: UploadCompleteSummary) => void
) {
  const qc = useQueryClient();
  const { t } = useTranslation();

  // Keep latest qc/t/callback without re-creating the Uppy instance on every render.
  const qcRef = useRef(qc);
  qcRef.current = qc;
  const tRef = useRef(t);
  tRef.current = t;
  const onCompleteRef = useRef(onUploadComplete);
  onCompleteRef.current = onUploadComplete;

  const [isUploading, setIsUploading] = useState(false);
  const [hasFiles, setHasFiles] = useState(false);

  const uppy = useMemo(() => {
    const authToken = TokenExtension.get();

    const instance = new Uppy({
      autoProceed: false,
      restrictions: {
        maxNumberOfFiles: MAX_DATASET_FILES_PER_BATCH,
        maxFileSize: MAX_DATASET_FILE_SIZE,
        allowedFileTypes: ALL_DATASET_FORMATS
      }
    }).use(Tus, {
      endpoint: `${API_BASE_URL}/api/v3/files/`,
      retryDelays: [0, 3000, 5000, 10000, 20000, 30000, 60000],
      removeFingerprintOnSuccess: true,
      storeFingerprintForResuming: true,
      // 5 MiB exactly — the minimum S3 multipart part size, matching
      // use-upload.ts. See the note there before changing this.
      chunkSize: 5 * 1024 * 1024,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fingerprint: (file: any) =>
        Promise.resolve(
          [
            'tus-cm',
            String(modelId),
            file.name || '',
            file.type || '',
            String(file.size || ''),
            file.meta?.uuid || ''
          ].join('-')
        ),
      headers: { Authorization: `Bearer ${authToken.string}` },
      withCredentials: true
    });

    const syncHasFiles = () => setHasFiles(instance.getFiles().length > 0);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    instance.on('file-added', (file: any) => {
      instance.setFileMeta(file.id, {
        upload_target: 'custom_model',
        model_id: String(modelId),
        uuid: uuidv4()
      });
      syncHasFiles();
    });
    instance.on('file-removed', syncHasFiles);
    instance.on('upload', () => setIsUploading(true));
    instance.on('error', () => setIsUploading(false));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    instance.on('complete', (result: any) => {
      setIsUploading(false);

      qcRef.current.invalidateQueries(CUSTOM_MODEL_QUERY_KEYS.data(modelId));
      qcRef.current.invalidateQueries(CUSTOM_MODEL_QUERY_KEYS.detail(modelId));
      qcRef.current.invalidateQueries(CUSTOM_MODEL_QUERY_KEYS.list);

      const ok = result?.successful?.length ?? 0;
      const failed = result?.failed?.length ?? 0;
      const tr = tRef.current;
      if (failed > 0) {
        toast.error(
          tr('files-upload-partial', { ok, failed }) ||
            `${failed} file(s) failed to upload`
        );
      } else if (ok > 0) {
        toast.success(
          ok === 1
            ? tr('dataset-uploaded-successfully')
            : tr('files-uploaded-successfully', { count: ok }) ||
                `${ok} files uploaded successfully`
        );
      }

      // Let the caller react (e.g. auto-close the modal once everything lands).
      onCompleteRef.current?.({ ok, failed });
    });

    return instance;
    // Recreate only when the target model changes.
  }, [modelId]);

  // Tear down the instance (and its listeners) when the hook unmounts.
  useEffect(() => {
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (uppy as any).destroy?.();
    };
  }, [uppy]);

  /**
   * Start uploading the currently-selected files. `dataType` constrains the slot
   * (TRANSCRIPT/AUDIO); omit it to let the server auto-detect per file.
   */
  const startUpload = useCallback(
    async (dataType?: CustomModelDataType) => {
      // Empty string == auto-detect on the server.
      uppy.setMeta({ data_type: dataType ? String(dataType) : '' });
      await uppy.upload();
    },
    [uppy]
  );

  /** Clear all selected/queued files (used by Cancel / modal close). */
  const reset = useCallback(() => {
    uppy.cancelAll();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    uppy.getFiles().forEach((f: any) => uppy.removeFile(f.id));
    setHasFiles(false);
    setIsUploading(false);
  }, [uppy]);

  /** Queue files from outside the dashboard, for example files dropped on the page. */
  const addFiles = useCallback(
    (files: File[]) => {
      try {
        uppy.addFiles(
          files.map(file => ({
            source: 'drop',
            name: file.name,
            type: file.type,
            data: file
          }))
        );
      } catch {
        // Uppy reports files it refuses (type, size, count) in the dashboard
        // and then throws; the files it accepted are already queued.
      }
    },
    [uppy]
  );

  return { uppy, isUploading, hasFiles, startUpload, addFiles, reset };
}
